<template>
  <div :class="['card-checklist']">
    <div :class="['progress flex ai-center', list.checkboxes.data.length ? ' mb-md' : ' mb-lg']">
      <div class="title mr-md">{{ completedPercentageString }}</div>
      <div class="bar" style="width: 100%">
        <ion-progress-bar
          class="br-md"
          :value="completedPercentage / 100"
          :color="completedPercentage === 100 ? 'success' : 'medium'"
        />
      </div>
    </div>
    <VueDraggable
      v-if="list"
      v-model="list.checkboxes.data"
      group="checklist"
      direction="vertical"
      :disabled="isEditMode || isLoading || dragIsLoading"
      :class="[{ mobile: isAnyMobile }]"
      :animation="150"
      :scroll-speed="15"
      :scroll-sensitivity="100"
      :delay="isAnyMobile ? 150 : 0"
      @end="dragEnd"
    >
      <transition-group name="fade">
        <div v-for="item in list.checkboxes.data" :key="item.id">
          <form-check-box-editable
            :id="item.id"
            v-model:array-of-editable="arrayOfEditable"
            :checked="item.state"
            :text="item.text"
            :is-loading="isLoading"
            size="small"
            @on-delete="deleteItem(item.id)"
            @on-update-text="editCheckbox(item.id, $event)"
            @on-checked="markCheckbox(item.id, $event)"
          />
        </div>
      </transition-group>
    </VueDraggable>

    <div
      v-if="!isEditMode"
      class="controls flex gap-md mt-md mb-lg jc-between"
      :class="!list.checkboxes.data.length ? '' : 'ml-2xl'"
    >
      <ion-button :disabled="isLoading" mode="md" fill="solid" @click.stop="addCheckbox()">
        {{ $t('add') }}
        <icons-provider
          v-if="isLoading"
          slot="start"
          class="mr-md"
          :icon-props="{
            width: '16',
            height: '16',
            fill: 'var(--ion-color-custom-secondary)',
          }"
          :name="AppIconsEnum.CircleAnim"
        />
      </ion-button>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { ComputedRef, PropType } from 'vue';
import { computed, ref } from 'vue';

import { SortableEvent, VueDraggable } from 'vue-draggable-plus';

import { FormCheckBoxEditable, IconsProvider } from '@/components';
import { isAnyMobile } from '@/helpers';

import type { TaskManagementTaskChecklistItemModel, TaskManagementTaskChecklistModel } from '@/types';
import { IonProgressBar, IonButton } from '@ionic/vue';
import { useProjectsStore } from '@/store';
import { useI18n } from '@/i18n';
import { AppIconsEnum } from '@/enums';
import { reactiveComputed } from '@vueuse/core';

const props = defineProps({
  checklist: {
    type: Object as PropType<TaskManagementTaskChecklistModel>,
    required: true,
  },
  size: {
    type: String,
    default: '',
  },
});

//#region Variables
const projectsStore = useProjectsStore();
const isLoading = ref(false);
const dragIsLoading = ref(false);
const { t } = useI18n();
const arrayOfEditable = ref<number[]>([]);
const isEditMode: ComputedRef<boolean> = computed(() => !!arrayOfEditable.value.length);
const list = reactiveComputed(() => {
  return props.checklist;
});

const completedPercentage: ComputedRef<number> = computed(() => {
  const total = list.checkboxes.data.length;
  const completed = list.checkboxes.data.filter((item) => item.state).length;
  return total > 0 ? Math.round((completed / total) * 100) : 0;
});

const completedPercentageString: ComputedRef<string> = computed(() => `${completedPercentage.value}%`);
//#endregion

//#region Methods
const deleteItem = async (checkboxId: number) => {
  isLoading.value = true;
  await projectsStore.deleteChecklistItem(props.checklist.taskId, checkboxId);
  isLoading.value = false;
};

const addCheckbox = async () => {
  isLoading.value = true;
  await projectsStore.createChecklistItem({
    text: t('taskManagement.checklist.newItem'),
    state: false,
    taskId: props.checklist.taskId,
  });
  arrayOfEditable.value.push(list.checkboxes.data[list.checkboxes.data.length - 1].id);
  isLoading.value = false;
};

const editCheckbox = async (checkboxId: number, text: string) => {
  isLoading.value = true;
  await projectsStore.updateChecklistItem({ checkboxId, taskId: props.checklist.taskId, text });
  isLoading.value = false;
};

const markCheckbox = async (checkboxId: number, state: boolean) => {
  isLoading.value = true;
  await projectsStore.markChecklistItem({ checkboxId, taskId: props.checklist.taskId, state });
  isLoading.value = false;
};

const dragEnd = async (e: SortableEvent & { data?: TaskManagementTaskChecklistItemModel }) => {
  dragIsLoading.value = true;

  if (!e.data) return console.warn('[WARN] No data when drop checkbox');

  if (e.oldIndex === e.newIndex) return console.log('[LOG] Is a same position');

  const itemId = e?.data?.id;
  await projectsStore.moveCheckbox(props.checklist.taskId, itemId, list.checkboxes.data);

  dragIsLoading.value = false;
};
//#endregion
</script>

<style scoped lang="scss">
.progress {
  ion-progress-bar {
    height: app-padding(md);
  }
}
.controls {
  ion-button {
    @include resetStyleFromIonicButton;
    --border-radius: #{app-radius(md)};
    --background: var(--ion-color-custom-element-lighter);
    color: var(--ion-color-custom-secondary);
  }
}
@include fadeEnterTransition;
</style>
